/**
 * navConfig.ts — fuente única de verdad del menú lateral + las rutas protegidas.
 *
 * Reemplaza el árbol `menuItems` que vivía inline en `components/Sidebar.tsx`.
 * El Sidebar y `app/AppRoutes.tsx` se generan de acá.
 *
 * Reglas de visibilidad (`requires`):
 *   - profile / profileNot  → gating por perfil de la org (migración 150).
 *                             Lo aplica también GuardedRoute (redirige si no aplica).
 *   - can(p)                → predicado de permisos, extraído verbatim del Sidebar.
 *                             HOY solo controla la visibilidad en el menú (igual que
 *                             antes las rutas no estaban gateadas por permiso). Para
 *                             gatear también la ruta, GuardedRoute ya lo soporta —
 *                             basta con no filtrarlo en AppRoutes.
 *   - planModule           → el item se muestra pero "locked" si el plan no lo incluye.
 *
 * Ver docs/plans/2026-08-30-modular-monolith-reorg.md §4.
 */

import { lazy } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard, Server, Shield, Users as UsersIcon, UserCircle, DoorOpen, FileText,
  MonitorDot, BarChart3, Building2, Briefcase, BadgeCheck, CalendarClock,
  MapPin, CreditCard, UsersRound, CalendarDays, Upload, PieChart, Car, Truck,
} from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import type { OrgProfile } from '@/types';

export type Perms = ReturnType<typeof usePermissions>;

export interface NavRequires {
  /** visible solo si el perfil de la org está en la lista */
  profile?: OrgProfile[];
  /** oculto si el perfil de la org está en la lista */
  profileNot?: OrgProfile[];
  /** predicado de permisos (mismo que usaba el Sidebar) — controla visibilidad en el menú */
  can?: (p: Perms) => boolean;
  /** módulo del plan: el item aparece pero bloqueado si el plan no lo incluye */
  planModule?: string;
  /** con planModule bloqueado, no listar el item ni con candado (verticales que no son upsell de plan) */
  hideWhenLocked?: boolean;
}

export interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  element: React.LazyExoticComponent<React.ComponentType<any>>;
  requires?: NavRequires;
  /** ruta que existe pero NO se lista en el sidebar */
  hidden?: boolean;
}

export interface NavSection {
  key: string;
  title: string;
  icon: LucideIcon;
  requires?: NavRequires;
  items: NavItem[];
}

// ── Lazy pages ─────────────────────────────────────────────────────────────────
const Dashboard            = lazy(() => import('@/modules/reporting/pages/Dashboard'));
const Users                = lazy(() => import('@/modules/platform/pages/Users'));
const EndUsers             = lazy(() => import('@/modules/people/pages/EndUsers'));
const Sedes                = lazy(() => import('@/modules/workspace/pages/Sedes'));
const Roles                = lazy(() => import('@/modules/platform/pages/Roles'));
const Controllers          = lazy(() => import('@/modules/hardware/pages/Controllers'));
const AccessPoints         = lazy(() => import('@/modules/access/pages/AccessPoints'));
const Logs                 = lazy(() => import('@/modules/access/pages/Logs'));
const OperationalDashboard = lazy(() => import('@/modules/reporting/pages/OperationalDashboard'));
const TechnicalReports     = lazy(() => import('@/modules/reporting/pages/TechnicalReports'));
const CorporateDashboard   = lazy(() => import('@/modules/reporting/pages/CorporateDashboard'));
const Departments          = lazy(() => import('@/modules/people/pages/Departments'));
const Employees            = lazy(() => import('@/modules/people/pages/Employees'));
const Parking              = lazy(() => import('@/modules/workspace/pages/Parking'));
const VisitorAppointments  = lazy(() => import('@/modules/people/pages/VisitorAppointments'));
const Credentials          = lazy(() => import('@/modules/credentials/pages/Credentials'));
const AccessGroups         = lazy(() => import('@/modules/access/pages/AccessGroups'));
const Spaces               = lazy(() => import('@/modules/workspace/pages/Spaces'));
const BulkImportHub        = lazy(() => import('@/modules/people/pages/BulkImportHub'));
const BulkEmployeeImport   = lazy(() => import('@/modules/people/pages/BulkEmployeeImport'));
const BulkCredentialImport = lazy(() => import('@/modules/workspace/pages/BulkCredentialImport'));
const BulkDepartmentImport = lazy(() => import('@/modules/people/pages/BulkDepartmentImport'));
const DispatchAgenda        = lazy(() => import('@/modules/dispatch/pages/DispatchAgenda'));
const Reports              = lazy(() => import('@/modules/reporting/pages/Reports'));
const Profile              = lazy(() => import('@/modules/platform/pages/Profile'));
const Preferences          = lazy(() => import('@/modules/platform/pages/Preferences'));
const Hubs                 = lazy(() => import('@/modules/hardware/pages/Hubs'));
const DeviceGroups         = lazy(() => import('@/modules/hardware/pages/DeviceGroups'));
const Bookings             = lazy(() => import('@/modules/workspace/pages/Bookings'));

/** La familia de perfiles que comparte la sección "Cowork / Corporativo". */
export const CORPORATE_FAMILY: OrgProfile[] = ['company', 'building', 'cowork'];

// ── Item suelto (fuera de secciones) ──────────────────────────────────────────
export const DASHBOARD_ITEM: NavItem = {
  path: '/dashboard',
  label: 'Dashboard',
  icon: LayoutDashboard,
  element: Dashboard,
  requires: { can: (p) => p.viewDashboard },
};

// ── Secciones colapsables ─────────────────────────────────────────────────────
export const NAV: NavSection[] = [
  {
    key: 'control',
    title: 'Centro de Control',
    icon: Server,
    // 'core' → siempre visible (sin gate de perfil)
    items: [
      { path: '/users',                 label: 'Usuarios de Panel',   icon: UsersIcon,    element: Users,
        requires: { can: (p) => p.hasPermission('users', 'view') } },
      { path: '/end-users',             label: 'Usuarios Finales',    icon: UserCircle,   element: EndUsers,
        requires: { can: (p) => p.hasPermission('users', 'view') } },
      { path: '/sedes',                 label: 'Sedes',               icon: MapPin,       element: Sedes,
        requires: { can: (p) => p.hasPermission('sedes', 'view') } },
      { path: '/roles',                 label: 'Roles',               icon: Shield,       element: Roles,
        requires: { can: (p) => p.hasPermission('roles', 'view') } },
      { path: '/controllers',           label: 'Controladoras',       icon: Server,       element: Controllers,
        requires: { can: (p) => p.hasPermission('controllers', 'view') } },
      { path: '/access-points',         label: 'Puntos de Acceso',    icon: DoorOpen,     element: AccessPoints,
        requires: { can: (p) => p.hasPermission('access_points', 'view') } },
      { path: '/logs',                  label: 'Logs',                icon: FileText,     element: Logs,
        requires: { can: (p) => p.hasPermission('logs', 'view') || p.hasPermission('audit', 'view'), planModule: 'access_logs' } },
      { path: '/operational-dashboard', label: 'Dashboard NOC',       icon: MonitorDot,   element: OperationalDashboard,
        requires: { can: (p) => p.hasPermission('dashboard', 'operational'), planModule: 'noc' } },
      { path: '/technical-reports',     label: 'Reportes Tecnicos',   icon: BarChart3,    element: TechnicalReports,
        requires: { can: () => false }, hidden: true },
    ],
  },
  {
    key: 'corporate',
    title: 'Cowork / Corporativo',
    icon: Building2,
    requires: { profile: CORPORATE_FAMILY },
    items: [
      { path: '/corporate-dashboard',   label: 'Dashboard Corporativo', icon: LayoutDashboard, element: CorporateDashboard,
        requires: { can: (p) => p.hasPermission('dashboard', 'business') } },
      { path: '/departments',           label: 'Departamentos',        icon: Briefcase,       element: Departments,
        requires: { can: (p) => p.hasPermission('departments', 'view') } },
      { path: '/employees',             label: 'Gestión de Personas',  icon: BadgeCheck,      element: Employees,
        requires: { can: (p) => p.hasPermission('employees', 'view') } },
      { path: '/parking',               label: 'Gestión de Estacionamientos', icon: Car,      element: Parking,
        requires: { can: (p) => p.hasPermission('parking', 'view'), planModule: 'parking' } },
      { path: '/visitor-appointments',  label: 'Citas de Visitantes',  icon: CalendarClock,   element: VisitorAppointments,
        requires: { can: (p) => p.hasPermission('visitors', 'view'), planModule: 'visitors' } },
      { path: '/credentials',           label: 'Credenciales',         icon: CreditCard,      element: Credentials,
        requires: { can: (p) => p.hasPermission('credentials', 'view') } },
      { path: '/access-groups',         label: 'Grupos de Acceso',     icon: UsersRound,      element: AccessGroups,
        requires: { can: (p) => p.hasPermission('access_groups', 'view') } },
      { path: '/dispatch',              label: 'Agenda de Despachos',  icon: Truck,           element: DispatchAgenda,
        requires: { can: (p) => p.hasPermission('dispatch', 'view'), planModule: 'bodegas', hideWhenLocked: true } },
      { path: '/spaces',                label: 'Espacios y Reservas',  icon: CalendarDays,    element: Spaces,
        requires: { can: (p) => p.hasPermission('spaces', 'view') || p.hasPermission('bookings', 'view'), planModule: 'bookings' } },
      { path: '/bulk-import',           label: 'Carga Masiva',         icon: Upload,          element: BulkImportHub,
        requires: { can: (p) => p.hasPermission('employees', 'bulk_import'), planModule: 'bulk_import' } },
      { path: '/reports',               label: 'Reportes',             icon: PieChart,        element: Reports,
        requires: { can: (p) => p.hasPermission('reports', 'view') || p.hasPermission('reports', 'security') || p.hasPermission('reports', 'hr') || p.hasPermission('reports', 'operations'), planModule: 'reports' } },
    ],
  },
];

// ── Rutas que existen pero no aparecen en el menú ─────────────────────────────
export const EXTRA_ROUTES: NavItem[] = [
  { path: '/profile',                 label: 'Mi Perfil',      icon: UserCircle, element: Profile,              hidden: true },
  { path: '/preferences',             label: 'Preferencias',   icon: UserCircle, element: Preferences,          hidden: true },
  { path: '/hubs',                    label: 'Hubs',           icon: Server,     element: Hubs,                 hidden: true },
  { path: '/device-groups',           label: 'Grupos de Dispositivos', icon: Server, element: DeviceGroups,    hidden: true },
  { path: '/bookings',                label: 'Reservas',       icon: CalendarDays, element: Bookings,           hidden: true },
  { path: '/bulk-import/employees',   label: 'Carga Empleados', icon: Upload,    element: BulkEmployeeImport,   hidden: true,
    requires: { can: (p) => p.hasPermission('employees', 'bulk_import') } },
  { path: '/bulk-import/credentials', label: 'Carga Credenciales', icon: Upload, element: BulkCredentialImport, hidden: true,
    requires: { can: (p) => p.hasPermission('employees', 'bulk_import') } },
  { path: '/bulk-import/departments', label: 'Carga Bodegas', icon: Upload, element: BulkDepartmentImport, hidden: true,
    requires: { can: (p) => p.hasPermission('departments', 'create'), planModule: 'bulk_import' } },
];

/** Todos los NavItem con ruta (para AppRoutes) — secciones + suelto + extras. */
export function allRouteItems(): NavItem[] {
  return [DASHBOARD_ITEM, ...NAV.flatMap((s) => s.items), ...EXTRA_ROUTES];
}
